import { DownloadOutlined } from "@ant-design/icons";
import { Button, Col, Row, Spin } from "antd";
import { FormInstance } from "antd/lib/form";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import React, { useState, useRef } from "react";
import { FileOption, FormFieldConfig } from "../helpers/shared-interfaces";
import { convertFileOptionsToSelectOptions } from "../helpers/utilities";
import BaseForm from "./Form/BaseForm";
import ModalCreator from "./ModalCreator";

interface Props {
  model: string;
  title?: string;
  buttonText?: string;
  fileType?: string;
  fileOptions?: FileOption[];
  onSuccess?: () => void;
  size?: SizeType;
  className?: string;
  disabled?: boolean;
}

const ImportData: React.FC<Props> = ({
  model,
  title,
  buttonText,
  fileType = "",
  fileOptions,
  onSuccess,
  size = "large",
  className = "btn-secondary",
  disabled = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [currentFileType, setCurrentFileType] = useState(fileOptions ? "" : fileType);
  const [fieldsToHide, setFieldsToHide] = useState(fileOptions ? ["datasetFile"] : []);
  const formRef = useRef<FormInstance>(null);

  const toggleModal = () => {
    setShowModal(!showModal);
    if (!showModal && fileOptions) {
      setCurrentFileType("");
      setFieldsToHide(["datasetFile"]);
    }
    if (showModal && onSuccess) {
      onSuccess();
    }
  };

  const handleChangeDatasetType = (value: string) => {
    const option = fileOptions?.find((opt) => opt.key === value);
    if (option) {
      setCurrentFileType(option.key);
      setFieldsToHide([]);
    }
  };

  const fields: FormFieldConfig[] = [
    ...(fileOptions && fileOptions.length > 0
      ? [
          {
            name: "datasetType",
            label: "Dataset Type",
            inputType: "select",
            placeholder: "Dataset Type",
            rule: "string",
            required: true,
            colConfig: { span: 24 },
            changeHandler: handleChangeDatasetType,
            selectOptions: convertFileOptionsToSelectOptions(fileOptions),
          } as FormFieldConfig,
        ]
      : []),
    {
      name: "datasetFile",
      label: "File Upload",
      inputType: "fileUpload",
      defaultFileList: [],
      placeholder: fileOptions ? "Dataset Type" : `Upload ${model} Data`,
      rule: "string",
      required: true,
      colConfig: { span: 24 },
      fileUploadProps: {
        type: currentFileType,
        opportunityId: null,
        isTriggeredManually: true,
        toggleModal: toggleModal,
      },
    } as FormFieldConfig,
  ];

  const modalTitle = title || `Import ${model} Data`;
  const btnText = buttonText || `Import ${model} Data`;

  return (
    <>
      <Button
        className={className}
        size={size}
        icon={<DownloadOutlined />}
        onClick={toggleModal}
        disabled={disabled}
      >
        {btnText}
      </Button>

      {showModal &&
        ModalCreator({
          title: modalTitle,
          okType: "primary",
          okText: "Done",
          onOkay: toggleModal,
          showModal: showModal,
          toggleModal: toggleModal,
          showFooter: false,
          inscription: (
            <Spin spinning={false}>
              <Row>
                <Col span={24} style={{ marginBottom: "20px" }}>
                  <div style={{ marginTop: "10px" }}>
                    <BaseForm
                      layout="vertical"
                      reference={formRef}
                      fields={fields}
                      fieldsToHide={fieldsToHide}
                    />
                    {fileOptions && fileOptions.length > 0 && (
                      <div style={{ marginTop: "10px", fontSize: "16px" }}>
                        <strong>Note:</strong> All bulk upload files must be in <strong>.csv</strong> format.
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Spin>
          ),
        })}
    </>
  );
};

export default ImportData;
